import React from 'react';

import { styled } from '@mui/material/styles';
import Checkbox from '@mui/material/Checkbox';
import KImage from './KImage';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import Button from '@mui/material/Button';
import swal from 'sweetalert';
import Client from './Client';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#273442",
    color: theme.palette.common.white,
    paddingLeft: "4px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingLeft: "4px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': { border: 0 },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* Skip resize re-renders unless content really changed */
const imagesSig = (arr) => {
  if (!Array.isArray(arr)) return "len:0";
  return "len:" + arr.length + "|" + arr.map(it =>
    [
      it.iref,
      it.image,
      it.isMain ? 1 : 0,
      it.isSalesApp ? 1 : 0,   // <- include sales app flag so UI re-renders
      it.name
    ].filter(Boolean).join(":")
  ).join(",");
};

/* NEW: lightweight wrapper that prevents KImage from re-rendering while typing */
const MemoKImage = React.memo(
  function MemoKImage(props) {
    return <KImage {...props} />;
  },
  (prev, next) => {
    // Only re-render if edit flag, image data, iref, or arrange changed.
    // Ignore imageText (name typing) and saveBack identity.
    return (
      prev.edit    === next.edit &&
      prev.image   === next.image &&
      prev.iref    === next.iref &&
      prev.arrange === next.arrange
    );
  }
);

class KImagesTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client ( "none" ),
      edit: {},                  // keyed map
      images: [],
      options: { },
      canvas: null,
      imageModifIdx: 0,
      saveURL: "",
      parentIref: "",
      showConfirm: false,
      removeIref: "",
      removeURL: "",
      addNew: false,
    };

    this.sendChangedState = this.sendChangedState.bind(this);
    this.saveBack = this.saveBack.bind(this);
    this.changeValue = this.changeValue.bind(this);
    this.editor = React.createRef();
    this.handleEditClick = this.handleEditClick.bind(this);
    this.changeName = this.changeName.bind(this);
    this.changeSalesApp = this.changeSalesApp.bind(this); // <- new
    this.toggleAdd = this.toggleAdd.bind(this);
    this.removeImage = this.removeImage.bind(this);
    this.removeConfirm = this.removeConfirm.bind(this);
    this.removeCancel = this.removeCancel.bind(this);
    this.showConfirmDialog = this.showConfirmDialog.bind(this);

    // Stable image prop cache for KImage
    this._imgMemo = {};
  }

  /* build stable image object for KImage; only changes if src changes */
  getImgObj = (image) => {
    const key = image.iref || 'newImage';
    const src = image.image || "";
    const cached = this._imgMemo[key];
    if (!cached || cached.data !== src) {
      this._imgMemo[key] = { data: src, height: 128 };
    }
    return this._imgMemo[key];
  };

  componentDidMount() {
    if ( this.props.images ) this.setState ( { images: this.props.images } );
    if ( this.props.saveURL ) this.setState ( { saveURL: this.props.saveURL } );
    if ( this.props.removeURL ) this.setState ( { removeURL: this.props.removeURL } );
    if ( this.props.parentIref ) this.setState ( { parentIref: this.props.parentIref } );

    let editMap = {};
    if (this.props.images && this.props.images.length > 0) {
      this.props.images.forEach(el => { editMap[el.iref] = false; });
    }
    this.setState({ edit: editMap });
  }

  componentDidUpdate ( prevProps ) {
    const imagesChanged = imagesSig(prevProps.images || []) !== imagesSig(this.props.images || []);
    const saveUrlChanged = prevProps.saveURL !== this.props.saveURL;
    const removeUrlChanged = prevProps.removeURL !== this.props.removeURL;
    const parentIrefChanged = prevProps.parentIref !== this.props.parentIref;

    if (imagesChanged || saveUrlChanged || removeUrlChanged || parentIrefChanged) {
      if ( this.props.images ) this.setState ( { images: this.props.images } );
      if ( this.props.saveURL ) this.setState ( { saveURL: this.props.saveURL } );
      if ( this.props.removeURL ) this.setState ( { removeURL: this.props.removeURL } );
      if ( this.props.parentIref ) this.setState ( { parentIref: this.props.parentIref } );

      // preserve existing edit flags; add defaults for new images
      let editMap = { ...(this.state.edit || {}) };
      if ( this.props.images && this.props.images.length > 0 ) {
        this.props.images.forEach(el => {
          if ( editMap[el.iref] === undefined ) editMap[el.iref] = false;
        });
      }
      this.setState ( { edit: editMap } );
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const propsChanged =
      imagesSig(this.props.images || []) !== imagesSig(nextProps.images || []) ||
      this.props.saveURL !== nextProps.saveURL ||
      this.props.removeURL !== nextProps.removeURL ||
      this.props.parentIref !== nextProps.parentIref ||
      this.props.edit !== nextProps.edit;

    const stateChanged =
      imagesSig(this.state.images || []) !== imagesSig(nextState.images || []) ||
      JSON.stringify(this.state.edit) !== JSON.stringify(nextState.edit) ||
      this.state.showConfirm !== nextState.showConfirm ||
      this.state.removeIref !== nextState.removeIref ||
      this.state.addNew !== nextState.addNew;

    return propsChanged || stateChanged;
  }

  sendChangedState ( e ) { }

  saveBack ( image, e ) {
    const iref = e;
    const uImages = (this.state.images || []).map(img =>
      img.iref === iref ? { ...img, image } : img
    );
    this.setState({ images: uImages });
  }

  pushData ( e ) {
    let image = null;
    let newMain = null;
    const images = this.state.images;
    for ( let i = 0; i < images.length; i++ ) {
      if ( images[ i ].iref === e ) image = images[ i ];
      if ( images[ i ].isMain === true ) newMain = images[ i ].iref;
    }
    image["newMain"] = newMain;
    image["parentIref"] = this.state.parentIref;

    const rdata = this.state.client.pushData(this.state.saveURL, image);
    if ( rdata ) {
      if ( rdata.status === "OK" ) {
        for ( let i = 0; i < images.length; i++ ) {
          if ( images[ i ].iref === e ) images[ i ].iref = rdata.iref;
        }
        this.setState({ images });

        if (e === "newImage") {
          const uEdit = { ...this.state.edit };
          delete uEdit["newImage"];
          uEdit[rdata.iref] = false;
          this.setState({ edit: uEdit });
        }

        this.handleEditClick(e);
        swal("Success", "Successfully added/modified image ! (" + rdata.iref + ")", "success", {
          buttons: false, timer: 2000,
        }).then(() => {
          this.setState({ addNew: false });
          this.props.imageUpdated();
          this.setState({ somethingHasChanged: false });
        });
      } else {
        if ( rdata.message ) this.setState({ errorMessage: rdata.message });
        swal("Error", "Add/Update image failed !", "error");
      }
    }
  }

  changeName ( e ) {
    const { id, value } = e.target;
    const uImages = (this.state.images || []).map(img =>
      img.iref === id ? { ...img, name: value } : img
    );
    this.setState({ images: uImages });
  }

  changeValue ( e ) {
    const { id } = e.target;
    const images = (this.state.images || []).map(img => ({
      ...img,
      isMain: img.iref === id
    }));
    this.setState({ images });
  }

  changeSalesApp ( e ) {
    const { id, checked } = e.target;
    const images = (this.state.images || []).map(img =>
      img.iref === id ? { ...img, isSalesApp: !!checked } : img
    );
    this.setState({ images });
  }

  handleEditClick ( e ) {
    const imgRef = e;
    const uEdit = { ...(this.state.edit || {}) };
    uEdit[imgRef] = !uEdit[imgRef];
    this.setState({ edit: uEdit });
  }

  toggleAdd ( ) {
    const addNew = !this.state.addNew;
    this.setState({ addNew });

    let uImages = Array.isArray(this.state.images) ? [ ...this.state.images ] : [];
    const uEdit = { ...this.state.edit };
    if ( addNew ) {
      uImages.unshift({ iref: "newImage" });
      uEdit["newImage"] = true;
    } else {
      if (uImages.length > 0 && uImages[0].iref === "newImage") {
        uImages = uImages.slice(1);
      }
      delete uEdit["newImage"];
    }
    this.setState({ images: uImages, edit: uEdit });
  }

  removeImage ( e ) {
    const images = [ ...this.state.images ];
    const edit = { ...this.state.edit };
    const rdata = this.state.client.pushData(this.state.removeURL, { iref: e });
    if ( rdata ) {
      if ( rdata.status === "OK" ) {
        const idx = images.findIndex(im => im.iref === e);
        if (idx !== -1) images.splice(idx, 1);
        delete edit[e];
        this.setState({ images, edit });
        this.handleEditClick(e);
        swal("Success", "Successfully added/modified image ! (" + rdata.iref + ")", "success", {
          buttons: false, timer: 2000,
        }).then(() => {
          this.props.imageUpdated();
          this.setState({ somethingHasChanged: false });
        });
      } else {
        if ( rdata.message ) this.setState({ errorMessage: rdata.message });
        swal("Error", "Add/Update image failed !", "error");
      }
    }
  }

  showConfirmDialog ( e ) {
    this.setState({ showConfirm: true, removeIref: e });
  }

  removeConfirm () {
    const removeIref = this.state.removeIref;
    this.removeImage(removeIref);
    this.setState({ showConfirm: false, removeIref: "" });
  }

  removeCancel () {
    this.setState({ showConfirm: false, removeIref: "" });
  }

  render() {
    let rows = {};
    if ( this.state.images ) {
      rows = this.state.images.map((image, idx) => {
        const rowKey = image.iref || image.id || `${image.name || 'image'}-${idx}`;
        const kImage = this.getImgObj(image); // stable object
        const showConfirm = !!this.state.showConfirm;

        return (
          <TableRow key={rowKey}>
            <StyledTableCell>
              <MemoKImage
                iref={image.iref}
                edit={this.state.edit[ image.iref ]}
                image={kImage}
                imageText={image.name}  // safe: ignored in memo compare
                saveBack={this.saveBack} // safe: ignored in memo compare
                arrange="flex"
              />
            </StyledTableCell>
            <StyledTableCell>
              {this.state.edit[ image.iref ] ? (
                <TextField
                  id={image.iref}
                  label="Name"
                  value={image.name || ''}
                  variant="outlined"
                  onChange={this.changeName}
                  sx={{minWidth: "90%", marginTop: 1}}
                  onFocus={event => { event.target.select(); }}
                  inputProps={{maxLength:"100"}}
                />
              ) : (
                image.name
              )}
            </StyledTableCell>
            <StyledTableCell>
              <Checkbox
                id={image.iref}
                color="success"
                checked={!!image.isMain}
                onChange={this.changeValue}
                disabled={!this.state.edit[ image.iref ]}
              >
                Main
              </Checkbox>
            </StyledTableCell>
            <StyledTableCell>
              <Checkbox
                id={image.iref}
                checked={!!image.isSalesApp}
                onChange={this.changeSalesApp}
                disabled={!this.state.edit[ image.iref ]}
              >
                Sales app
              </Checkbox>
            </StyledTableCell>
            <StyledTableCell align="right" sx={{padding: "1px"}}>
              <IconButton aria-label="settings" id={"ib-" + image.iref} onClick={() => {this.handleEditClick(image.iref);}}>
                <EditIcon
                  id={image.iref}
                  sx={{ color: this.state.edit[ image.iref ] ? "#8f241d" : "#e3e3e5" }}
                />
              </IconButton>
              {this.state.edit[ image.iref ]  ? (
                <IconButton aria-label="settings" id={image.iref} onClick={() => {this.pushData(image.iref);}}>
                  <SaveIcon sx={{ color: "#4287f5" }} />
                </IconButton>
              ) : ""}

              {this.state.edit[ image.iref ]  ? (
                <IconButton aria-label="settings" id={image.iref} onClick={() => {this.showConfirmDialog(image.iref);}}>
                  <DeleteForeverIcon sx={{ color: "#8f241d" }} />
                </IconButton>
              ) : ""}

              <Dialog
                open={showConfirm}
                TransitionComponent={Transition}
                keepMounted
                onClose={this.removeCancel}
                aria-describedby="alert-dialog-slide-description"
              >
                <DialogTitle>{"Remove image ?"}</DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-slide-description">
                    This action will delete the image and it is irreversible.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={this.removeCancel}>No</Button>
                  <Button onClick={() => {this.removeConfirm();}}>Yes</Button>
                </DialogActions>
              </Dialog>

            </StyledTableCell>
          </TableRow>
        );
      });
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: "middle", justifyContent:"center", alignItems: "center", paddingTop: "2px" }}>
        <Table sx={{ minWidth: 700, textAlign: "middle", justifyContent:"center" }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <IconButton aria-label="Add Image" title="Add Image" onClick={() => this.toggleAdd()}>
                  <Avatar alt="Add Image" sx={{ width: 24, height: 24, color: "#273442" }}>
                    {this.state.addNew ? <FaMinusCircle/> : <FaPlusCircle/>}
                  </Avatar>
                </IconButton>
                Image
              </StyledTableCell>
              <StyledTableCell align="left">Name</StyledTableCell>
              <StyledTableCell align="left">is Main</StyledTableCell>
              <StyledTableCell align="left">Sales app</StyledTableCell>
              <StyledTableCell align="right">Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows}
          </TableBody>
        </Table>
      </Box>
    );
  }
}

export default KImagesTable;
